/**
 * Cards Store - Mellow flashcards with FSRS spaced repetition
 * Simple direct Supabase calls (like chats$) - no complex local-first sync
 */

import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createNewCard, countDueCards } from '../utils/fsrs';

// Simple cards state (like chats$)
export const cards$ = observable({
  items: [],        // All cards for current user
  isLoading: false,
});

// Simple decks state
export const decks$ = observable({
  items: [],
  defaultDeckId: null,
});

/**
 * Fetch all cards for current user, filtered by language
 */
export const fetchCards = async (languageCode) => {
  const user = auth$.user.get();
  if (!user) {
    cards$.items.set([]);
    return;
  }

  cards$.isLoading.set(true);

  let query = supabase
    .from('mellow_cards')
    .select('*')
    .eq('user_id', user.id)
    .eq('deleted', false);

  // Filter by language if provided
  if (languageCode) {
    query = query.eq('language_code', languageCode);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch cards:', error);
    cards$.isLoading.set(false);
    return;
  }

  cards$.items.set(data || []);
  cards$.isLoading.set(false);
};

/**
 * Fetch decks and get/create default deck
 */
export const fetchDecks = async () => {
  const user = auth$.user.get();
  if (!user) {
    decks$.items.set([]);
    decks$.defaultDeckId.set(null);
    return null;
  }

  const { data, error } = await supabase
    .from('mellow_decks')
    .select('*')
    .eq('user_id', user.id)
    .eq('deleted', false);

  if (error) {
    console.error('Failed to fetch decks:', error);
    return null;
  }

  decks$.items.set(data || []);

  // Return existing default deck or create one
  if (data && data.length > 0) {
    decks$.defaultDeckId.set(data[0].id);
    return data[0];
  }

  // Create default deck
  const { data: newDeck, error: createError } = await supabase
    .from('mellow_decks')
    .insert({
      user_id: user.id,
      name: 'Default',
      new_cards_per_day: 20,
      review_cards_per_day: 200,
    })
    .select()
    .single();

  if (createError) {
    console.error('Failed to create default deck:', createError);
    return null;
  }

  decks$.items.set([newDeck]);
  decks$.defaultDeckId.set(newDeck.id);
  return newDeck;
};

/**
 * Get or create the default deck for current user
 */
export const getOrCreateDefaultDeck = async () => {
  const deckId = decks$.defaultDeckId.get();
  if (deckId) {
    const items = decks$.items.get();
    return items.find(d => d.id === deckId);
  }
  return await fetchDecks();
};

/**
 * Get all cards as array (from local state)
 */
export const getCardsArray = () => {
  return cards$.items.get() || [];
};

/**
 * Get card by ID
 */
export const getCardById = (cardId) => {
  const items = cards$.items.get() || [];
  return items.find(c => c.id === cardId);
};

/**
 * Check if a card with given front text already exists
 */
export const cardExistsByFront = (front) => {
  const cards = getCardsArray();
  return cards.some(c => c.front?.toLowerCase() === front?.toLowerCase());
};

/**
 * Count due cards
 */
export const getDueCount = () => {
  return countDueCards(getCardsArray());
};

/**
 * Create a new card - direct Supabase insert (like chats$)
 */
export const createCard = async ({ front, back, cardType, translation, pronunciation, sourceChatId, sourceMessageIndex, languageCode }) => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  // Get or create default deck
  const deck = await getOrCreateDefaultDeck();
  if (!deck) throw new Error('Failed to get/create deck');

  const fsrsFields = createNewCard();

  const { data, error } = await supabase
    .from('mellow_cards')
    .insert({
      deck_id: deck.id,
      user_id: userId,
      front: front.trim(),
      back: back.trim(),
      card_type: cardType || null,
      translation: translation || null,
      pronunciation: pronunciation || null,
      language_code: languageCode || null,
      ...fsrsFields,
      source_chat_id: sourceChatId || null,
      source_message_index: sourceMessageIndex ?? null,
    })
    .select()
    .single();

  if (error) throw error;

  // Add to local state
  const items = cards$.items.get();
  cards$.items.set([data, ...items]);

  return data;
};

/**
 * Update card after review (FSRS scheduling) - direct Supabase update
 */
export const updateCardScheduling = async (cardId, schedulingData) => {
  const updates = {
    due: schedulingData.due,
    stability: schedulingData.stability,
    difficulty: schedulingData.difficulty,
    elapsed_days: schedulingData.elapsed_days,
    scheduled_days: schedulingData.scheduled_days,
    reps: schedulingData.reps,
    lapses: schedulingData.lapses,
    state: schedulingData.state,
    last_review: schedulingData.last_review,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('mellow_cards')
    .update(updates)
    .eq('id', cardId);

  if (error) throw error;

  // Update local state
  const items = cards$.items.get();
  cards$.items.set(items.map(c =>
    c.id === cardId ? { ...c, ...updates } : c
  ));
};

/**
 * Delete a card (soft delete) - direct Supabase update
 */
export const deleteCard = async (cardId) => {
  const { error } = await supabase
    .from('mellow_cards')
    .update({ deleted: true, updated_at: new Date().toISOString() })
    .eq('id', cardId);

  if (error) throw error;

  // Remove from local state
  const items = cards$.items.get();
  cards$.items.set(items.filter(c => c.id !== cardId));
};

/**
 * Unsave/remove a card by front text
 */
export const removeCardByFront = async (front) => {
  const cards = getCardsArray();
  const card = cards.find(c => c.front?.toLowerCase() === front?.toLowerCase());
  if (card) {
    await deleteCard(card.id);
    return true;
  }
  return false;
};

